import { COMPONENT_VALUE_TYPES } from 'shared/constants';
import {
  enrichPropsSchemaItems,
  isDateTimePropKey,
  isDateTimePropValue,
  normalizeDateTimeDefaultValue,
  upgradeDateTimePropSchemaItem,
} from 'shared/socialIconEnumOptions';

/**
 * propsJsonImport
 * ----------------
 * Convert a pasted JSON blob into a Custom Widget `propsSchema` + `defaultProps`
 * pair, and serialize the current props back to JSON.
 *
 * Accepts three shapes:
 *   1. A full export object:  { "propsSchema": [...], "defaultProps": {...} }
 *   2. A bare values object:  { "heroImage": "...", "content": {...}, "items": [...] }
 *      (i.e. just `defaultProps`) — a schema is inferred from the values.
 *   3. A bare schema array:   [ { "key": "...", "type": "...", ... }, ... ]
 *
 * The returned `defaultProps` always mirrors the pasted values exactly, so the
 * live preview is correct immediately; the returned `schema` carries matching
 * `defaultValue`s so the per-field editors display them too.
 */

export interface PropsSchemaItem {
  key: string;
  label: string;
  type: string;
  defaultValue?: any;
  options?: { label: string; value: string }[];
  children?: PropsSchemaItem[];
}

export interface ParsedProps {
  schema: PropsSchemaItem[];
  defaultProps: Record<string, any>;
}

const T = COMPONENT_VALUE_TYPES;

const KNOWN_TYPES: string[] = [
  T.TEXT, T.TEXTAREA, T.BOOLEAN, T.S3_ASSET_URL, T.COLOR_PICKER,
  T.HTML, T.RICHTEXT, T.TINYMCE, T.ENUM, T.ARRAY, T.OBJECT,
  T.DATE, T.DATETIME,
  T.LIVESTREAM_CONTROL, T.INFO,
];

const IMAGE_KEY_RE = /(image|img|logo|icon|photo|thumb|thumbnail|avatar|asset|banner|background|^bg$|cover|poster|media)/i;
const IMAGE_VAL_RE = /^data:image\/|\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i;
const LINK_KEY_RE = /(link|href|url|cta|slug|route|path)/i;
const COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Turn `featuredImage` / `col1_heading` into `Featured Image` / `Col1 Heading`. */
export const humanizeKey = (key: string): string => {
  const spaced = String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Type-appropriate empty value (mirrors PropsSchemaEditor.getDefaultValueForType). */
const emptyForType = (type: string): any => {
  switch (type) {
    case T.BOOLEAN: return false;
    case T.ARRAY: return [];
    case T.OBJECT: return {};
    case T.COLOR_PICKER: return '#000000';
    case T.DATE:
    case T.DATETIME:
      return {};
    default: return '';
  }
};

const inferScalarType = (key: string, value: string): string => {
  const v = value.trim();
  if (COLOR_RE.test(v)) return T.COLOR_PICKER;
  if (IMAGE_VAL_RE.test(v)) return T.S3_ASSET_URL;
  if (IMAGE_KEY_RE.test(key) && !LINK_KEY_RE.test(key)) return T.S3_ASSET_URL;
  if (v.includes('<') && v.includes('>')) return T.HTML; // e.g. "Line one<br>Line two"
  if (v.includes('\n') || v.length > 80) return T.TEXTAREA;
  return T.TEXT;
};

/**
 * Infer a schema item for one value. `bakeDefault` controls whether the value
 * itself is stored as the item's `defaultValue` (true for top-level props and
 * object fields) or replaced with a type-empty default (used for the *field
 * definitions* of array items, where the real data lives on the array's
 * `defaultValue` instead).
 */
const valueToSchemaItem = (key: string, value: any, bakeDefault: boolean): PropsSchemaItem => {
  const label = humanizeKey(key);

  if (typeof value === 'boolean') {
    return { key, label, type: T.BOOLEAN, defaultValue: bakeDefault ? value : false };
  }
  if (typeof value === 'number') {
    return { key, label, type: T.TEXT, defaultValue: bakeDefault ? String(value) : '' };
  }
  if (value === null || value === undefined) {
    if (isDateTimePropKey(key)) {
      return { key, label, type: T.DATETIME, defaultValue: bakeDefault ? {} : {} };
    }
    return { key, label, type: T.TEXT, defaultValue: '' };
  }
  if (Array.isArray(value)) {
    return {
      key, label, type: T.ARRAY,
      defaultValue: bakeDefault ? value : [],
      children: inferArrayChildren(value),
    };
  }
  if (typeof value === 'object') {
    if (isDateTimePropKey(key) || isDateTimePropValue(value)) {
      return {
        key,
        label,
        type: T.DATETIME,
        defaultValue: bakeDefault ? normalizeDateTimeDefaultValue(value) : {},
      };
    }
    return {
      key, label, type: T.OBJECT,
      defaultValue: bakeDefault ? value : {},
      children: objectToSchema(value as Record<string, any>),
    };
  }
  const str = String(value);
  return { key, label, type: inferScalarType(key, str), defaultValue: bakeDefault ? str : '' };
};

/** Field definitions for the items of an array (union of object keys, in first-seen order). */
const inferArrayChildren = (arr: any[]): PropsSchemaItem[] => {
  const objectItems = arr.filter((it) => it && typeof it === 'object' && !Array.isArray(it));
  if (objectItems.length === 0) return []; // primitive array → reference items with {{.}} in HTML
  const keys: string[] = [];
  for (const it of objectItems) {
    for (const k of Object.keys(it)) if (!keys.includes(k)) keys.push(k);
  }
  return keys.map((k) => {
    const sample = objectItems.find((it) => it[k] !== null && it[k] !== undefined);
    // Use the sample only to infer the TYPE; field defaults stay type-empty.
    return valueToSchemaItem(k, sample ? sample[k] : '', false);
  });
};

/** Build a schema from a plain values object (top-level props bake their defaults). */
export const objectToSchema = (obj: Record<string, any>): PropsSchemaItem[] =>
  Object.keys(obj).map((k) => valueToSchemaItem(k, obj[k], true));

/** Normalize a hand-written/exported schema array (fill labels, validate types, recurse). */
const normalizeSchema = (arr: any[]): PropsSchemaItem[] =>
  arr
    .filter((it) => it && typeof it === 'object' && typeof it.key === 'string' && it.key)
    .map((it) => {
      const type = KNOWN_TYPES.includes(it.type) ? it.type : T.TEXT;
      const out: PropsSchemaItem = {
        key: it.key,
        label: typeof it.label === 'string' && it.label ? it.label : humanizeKey(it.key),
        type,
      };
      if (type === T.ARRAY || type === T.OBJECT) {
        out.children = Array.isArray(it.children) ? normalizeSchema(it.children) : [];
        if (it.defaultValue !== undefined) out.defaultValue = it.defaultValue;
      } else {
        out.defaultValue = it.defaultValue !== undefined ? it.defaultValue : emptyForType(type);
      }
      // Preserve Select (dropdown) options — strings (SimpleFooter style) or {label,value}
      if (Array.isArray(it.options)) {
        out.options = it.options
          .map((o: any) => {
            if (typeof o === 'string' || typeof o === 'number') {
              const value = String(o);
              return { label: humanizeKey(value), value };
            }
            if (o && typeof o === 'object') {
              return { label: String(o.label ?? o.value ?? ''), value: String(o.value ?? o.label ?? '') };
            }
            return null;
          })
          .filter(Boolean) as { label: string; value: string }[];
      }
      return upgradeDateTimePropSchemaItem(out);
    });

/** Reconstruct defaultProps from a schema (mirrors WidgetBuilder.buildDefaultProps). */
export const buildDefaultsFromSchema = (schema: PropsSchemaItem[]): Record<string, any> => {
  const out: Record<string, any> = {};
  schema.forEach((item) => {
    if (item.type === T.ARRAY) {
      out[item.key] = item.defaultValue ?? [];
    } else if (item.type === T.OBJECT && item.children && item.children.length > 0) {
      out[item.key] = buildDefaultsFromSchema(item.children);
    } else {
      out[item.key] = item.defaultValue ?? emptyForType(item.type);
    }
  });
  return out;
};

/** Push values from a values object onto a schema's defaultValue fields (so editors show them). */
const mergeDefaultsIntoSchema = (schema: PropsSchemaItem[], values: Record<string, any>): void => {
  if (!values || typeof values !== 'object') return;
  schema.forEach((item) => {
    if (!(item.key in values)) return;
    const v = values[item.key];
    if (item.type === T.OBJECT && item.children) {
      mergeDefaultsIntoSchema(item.children, v || {});
    } else {
      item.defaultValue = v;
    }
  });
};

const stripTypename = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(stripTypename);
  if (obj && typeof obj === 'object') {
    const out: any = {};
    for (const k of Object.keys(obj)) if (k !== '__typename') out[k] = stripTypename(obj[k]);
    return out;
  }
  return obj;
};

/**
 * Parse pasted JSON into { schema, defaultProps }, or throw an Error with a
 * human-readable message describing why it could not be used.
 */
export const parsePropsJson = (text: string): ParsedProps => {
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('Paste some JSON first.');

  let parsed: any;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e: any) {
    throw new Error(`Invalid JSON: ${e?.message || 'could not parse'}`);
  }

  const finish = (schema: PropsSchemaItem[], defaultProps: Record<string, any>): ParsedProps => ({
    schema: enrichPropsSchemaItems(schema),
    defaultProps,
  });

  // Shape 3: a bare schema array
  if (Array.isArray(parsed)) {
    const schema = normalizeSchema(parsed);
    if (schema.length === 0) throw new Error('That array has no valid props (each needs a "key").');
    return finish(schema, buildDefaultsFromSchema(schema));
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Expected a JSON object (props) or array (schema).');
  }

  // Shape 1: a full export { propsSchema, defaultProps }
  if (Array.isArray(parsed.propsSchema)) {
    const schema = normalizeSchema(parsed.propsSchema);
    if (schema.length === 0) throw new Error('"propsSchema" has no valid props (each needs a "key").');
    const defaultProps =
      parsed.defaultProps && typeof parsed.defaultProps === 'object' && !Array.isArray(parsed.defaultProps)
        ? stripTypename(parsed.defaultProps)
        : buildDefaultsFromSchema(schema);
    mergeDefaultsIntoSchema(schema, defaultProps);
    return finish(schema, defaultProps);
  }

  // Shape 2: a bare values object (defaultProps)
  const values = stripTypename(parsed);
  const keys = Object.keys(values);
  if (keys.length === 0) throw new Error('That object is empty — nothing to import.');
  const schema = objectToSchema(values);
  return finish(schema, values);
};

/** Serialize current props as the canonical { propsSchema, defaultProps } JSON. */
export const exportPropsJson = (
  schema: PropsSchemaItem[],
  defaultProps: Record<string, any>,
): string =>
  JSON.stringify(
    { propsSchema: stripTypename(schema || []), defaultProps: stripTypename(defaultProps || {}) },
    null,
    2,
  );
